import { PreInterview, PreInterviewQuery } from '@dreryk/egabinet-pre-interview-frontend-api';
import { Survey, SurveyQuery, SurveyTemplate, SurveyTemplateQuery } from '@dreryk/egabinet-survey-frontend-api';

import { useCqrsFind, useCqrsGet } from '@dreryk/command-query-react-common';
import { LoaderSpinner, Message, MessageType } from '@dreryk/react-components';
import { Slot } from '@dreryk/watermelon';

import { appointmentsTranslationKeys } from '../../translations';

import { PreInterviewItem } from './PreInterviewPreviewItem.types';
import { mapPreInterviewItems } from './PreInterviewPreviewItems.utils';

const PREINTERVIEW_TEMPLATE_CODE = 'PREINTERVIEW';

type PreInterviewPreviewItemsLoaderRenderProps = {
   preInterviewItems: PreInterviewItem[];
   preInterview: PreInterview;
};

type PreInterviewPreviewItemsLoaderProps = {
   readonly appointmentId: number;
   readonly children: (renderProps: PreInterviewPreviewItemsLoaderRenderProps) => JSX.Element;
};

export const PreInterviewPreviewItemsLoader = (props: PreInterviewPreviewItemsLoaderProps): JSX.Element => {
   const { data: preInterview, loading: preInterviewLoading, error: preInterviewError } = useCqrsFind<PreInterview>(
      () => {
         const command = new PreInterviewQuery();
         command.appointmentIds = Slot.emptyIfNull([props.appointmentId]);
         return command;
      }
   );

   const { data: survey, loading: surveyLoading, error: surveyError } = useCqrsGet<Survey>(
      () => {
         const query = new SurveyQuery();

         if (preInterview) {
            query.id = preInterview.surveyId;
         }

         return query;
      },
      { enabled: Boolean(preInterview) }
   );

   const {
      data: preInterviewTemplate,
      loading: preInterviewTemplateLoading,
      error: preInterviewTemplateError,
   } = useCqrsGet<SurveyTemplate>(
      () => {
         const query = new SurveyTemplateQuery();
         query.templateCode = Slot.emptyIfNull(PREINTERVIEW_TEMPLATE_CODE);
         query.templateVersionId = Slot.emptyIfNull(survey?.surveyTemplateVersionId ?? null);
         query.published = Slot.emptyIfNull(true);
         return query;
      },
      { enabled: Boolean(survey?.surveyTemplateVersionId) }
   );

   if (surveyLoading || preInterviewTemplateLoading || preInterviewLoading) {
      return <LoaderSpinner />;
   }

   if (surveyError || preInterviewTemplateError || preInterviewError) {
      return (
         <Message
            testId={'preInterviewFetchError'}
            type={MessageType.NEGATIVE}
            label={appointmentsTranslationKeys.preInterview.failedToFetch}
            displayIndicator
         />
      );
   }

   return props.children({
      preInterviewItems: mapPreInterviewItems(preInterviewTemplate.questions, survey.answers),
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
      preInterview: preInterview!,
   });
};
